// Pending-mutation queue (§11c). A mutation that fails because the device is offline is stored with
// its owner user, business, target and idempotency key at creation time, and replayed later only by
// the same user after a fresh access check. Items owned by another account are held, never sent.
import { useSyncExternalStore } from 'react';
import { api, ApiError, isStaleOwner, newKey } from './api';
import { idbDel, idbGet, idbKeys, idbSet } from './idb';
import { isImpersonating } from './impersonation';

/** §6 Online-only writes: nothing done as a client is ever queued on this device. */
export const ACTING_NOT_QUEUED = "Couldn't send. Actions as a client can't be queued.";

export type PendingMutation = {
  id: string;
  ownerUserId: string;
  businessId: string;
  method: 'POST' | 'PATCH' | 'DELETE';
  path: string;
  body?: unknown;
  idempotencyKey: string;
  /** Human label: "Message to Harbor Dental", "Submit work order". */
  label: string;
  createdAt: number;
  /**
   * queued: will replay automatically. failed: the server refused it (conflict, validation) and the person
   * must Retry or Discard. no-access: the account lost access to the business; held until discarded.
   */
  status?: 'queued' | 'failed' | 'no-access';
  /** Last server message, shown next to failed items. */
  error?: string;
  attempts?: number;
};

const PREFIX = 'pending:';
let items: PendingMutation[] = [];
const listeners = new Set<() => void>();
const emit = () => {
  items = [...items];
  listeners.forEach((l) => l());
};

export async function loadPending(): Promise<void> {
  const keys = await idbKeys(PREFIX);
  const all = await Promise.all(keys.map((k) => idbGet<PendingMutation>(k)));
  items = all.filter((x): x is PendingMutation => !!x).sort((a, b) => a.createdAt - b.createdAt);
  emit();
}

const isOffline = (e: unknown) => e instanceof TypeError || (typeof navigator !== 'undefined' && navigator.onLine === false);

/**
 * Sends a mutation; if the network is down it is queued for replay and `{ queued: true }` is returned.
 * Server errors (4xx/5xx) are thrown, never queued.
 */
export async function sendOrQueue<T>(
  input: Omit<PendingMutation, 'id' | 'createdAt' | 'idempotencyKey'> & { idempotencyKey?: string; epoch?: number },
): Promise<{ queued: false; data: T } | { queued: true }> {
  const { epoch, ...m } = input;
  const full: PendingMutation = { ...m, id: newKey(), createdAt: Date.now(), idempotencyKey: m.idempotencyKey ?? newKey() };
  // Captured before dispatch: the identity at the time the rejection lands (e.g. after Exit) doesn't matter.
  const actingSnapshot = isImpersonating();
  if (actingSnapshot && typeof navigator !== 'undefined' && navigator.onLine === false) throw new ApiError(0, 'impersonation_offline', ACTING_NOT_QUEUED, null);
  try {
    // epoch: refused before dispatch (StaleOwnerError, never queued) if the account changed since the caller started.
    const data = await api<T>(full.method, full.path, full.body, { idempotencyKey: full.idempotencyKey, epoch });
    return { queued: false, data };
  } catch (e) {
    if (e instanceof ApiError || isStaleOwner(e)) throw e;
    // Acting as a client: any other failure (a fetch TypeError even while navigator.onLine) is never persisted.
    if (actingSnapshot) throw new ApiError(0, 'impersonation_offline', ACTING_NOT_QUEUED, e);
    if (!isOffline(e)) throw e;
    items.push(full);
    await idbSet(PREFIX + full.id, full);
    emit();
    return { queued: true };
  }
}

export async function discardPending(id: string) {
  items = items.filter((x) => x.id !== id);
  await idbDel(PREFIX + id);
  emit();
}

async function update(id: string, patch: Partial<PendingMutation>) {
  const i = items.findIndex((x) => x.id === id);
  if (i < 0) return;
  items[i] = { ...items[i], ...patch };
  await idbSet(PREFIX + id, items[i]);
  emit();
}

/** Server answers that may succeed later without the person doing anything. */
const retryable = (e: ApiError) => e.status >= 500 || e.status === 408 || e.status === 429;

/** true: allowed; false: definitely refused (403/404); 'unknown': could not tell (offline, 5xx, 401…). */
export type AccessCheck = boolean | 'unknown';

export type ReplayResult = { sent: number; failed: number; held: number };

/**
 * Replays this user's queued items, oldest first. Nothing is ever dropped silently:
 * - items owned by another account are skipped ("Saved by another account");
 * - a business the server definitely refuses (403/404) marks the item `no-access` (held, can be retried);
 * - an access check that can't be answered (offline, 5xx, 401) stops the run and keeps the item queued;
 * - offline or a retryable server error (5xx, 408, 429) stops the run and keeps the item queued;
 * - any other refusal (409 conflict, 400/422) marks it `failed` with the server message, for Retry/Discard;
 * - `stillOwner()` is checked before every send, so a run started by one account stops as soon as the
 *   signed-in account changes (the fence for old session work).
 * The idempotency key stored at creation prevents duplicates if a "failed" send had in fact landed.
 */
export async function replayPending(
  currentUserId: string,
  checkAccess: (businessId: string) => Promise<AccessCheck>,
  stillOwner: () => boolean = () => true,
): Promise<ReplayResult> {
  const r: ReplayResult = { sent: 0, failed: 0, held: 0 };
  const access = new Map<string, AccessCheck>();
  for (const m of [...items]) {
    if (m.ownerUserId !== currentUserId) continue;
    if (m.status && m.status !== 'queued') continue;
    if (!stillOwner()) break;
    if (!access.has(m.businessId)) access.set(m.businessId, await checkAccess(m.businessId).catch((): AccessCheck => 'unknown'));
    if (!stillOwner()) break;
    // A temporary failure of the check itself (offline, 5xx, session hiccup) keeps the item queued and
    // stops the run, preserving order. Only a definite refusal holds it as no-access.
    if (access.get(m.businessId) === 'unknown') break;
    if (access.get(m.businessId) === false) {
      await update(m.id, { status: 'no-access', error: 'You no longer have access to this business.' });
      r.held++;
      continue;
    }
    try {
      await api(m.method, m.path, m.body, { idempotencyKey: m.idempotencyKey });
      await discardPending(m.id);
      r.sent++;
    } catch (e) {
      if (isStaleOwner(e)) break; // account changed mid-send: keep it for its owner, record nothing
      if (!(e instanceof ApiError)) {
        if (isOffline(e)) break;
        await update(m.id, { attempts: (m.attempts ?? 0) + 1 });
        break;
      }
      if (retryable(e)) {
        await update(m.id, { attempts: (m.attempts ?? 0) + 1, error: e.message });
        break;
      }
      if (e.status === 401) break; // session ended: the session layer takes over; keep the item.
      if (e.status === 403 || e.status === 404) {
        await update(m.id, { status: 'no-access', error: e.message });
        r.held++;
        continue;
      }
      await update(m.id, { status: 'failed', error: e.message, attempts: (m.attempts ?? 0) + 1 });
      r.failed++;
    }
  }
  return r;
}

/** Puts a failed or no-access item back in the queue; the caller then runs replayPending (fresh access check). */
export async function requeuePending(id: string) {
  await update(id, { status: 'queued', error: undefined });
}

export function pendingSnapshot(): PendingMutation[] {
  return items;
}

const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => listeners.delete(l);
};
export function usePending(): PendingMutation[] {
  return useSyncExternalStore(subscribe, () => items, () => items);
}
export function clearPendingMemory() {
  items = [];
  emit();
}
